/**
 * User Growth API
 * Operational user-growth dashboard endpoints (admin only).
 */

import apiClient from './client';

export interface GrowthSummary {
  today_new_users: number;
  today_active_regular: number;
  today_active_total: number;
  new_users_7d: number;
  d1_retention_7d: number | null;
}

export interface DailyGrowthRow {
  date: string;
  new_users_ios: number;
  new_users_android: number;
  new_users_unknown: number;
  active_regular: number;
  active_team: number;
  cumulative_regular: number;
  cumulative_total: number;
}

export interface ActiveUserRow {
  date: string;
  user_id: string;
  display_name: string;
  classification: string;
  registered_at: string | null;
  is_new_user: boolean;
  platform: string | null;
  sessions: number;
  runs: number;
}

export interface GrowthFunnel {
  registered: number;
  created_self_chart: number;
  first_conversation: number;
  trial_started: number;
  paid: number;
  median_hours_to_chart: number | null;
}

export interface CohortRow {
  week_start: string;
  cohort_size: number;
  retention: number[];
}

export interface RetentionResponse {
  d7: number | null;
  d30: number | null;
  dau_mau: number | null;
  cohorts: CohortRow[];
}

/**
 * Shared window params: either preset `days`, or a custom closed range
 * `start_date`/`end_date` (YYYY-MM-DD, server timezone). When both are sent,
 * the backend gives precedence to start/end.
 */
export interface RangeParams {
  days?: number;
  start_date?: string;
  end_date?: string;
}

const BASE = '/user-growth';

export const userGrowthApi = {
  getSummary: async (): Promise<GrowthSummary> => {
    const response = await apiClient.get(`${BASE}/summary`);
    return response.data;
  },

  getDaily: async (range: RangeParams): Promise<DailyGrowthRow[]> => {
    const response = await apiClient.get(`${BASE}/daily`, { params: range });
    return response.data;
  },

  getActiveUsers: async (
    params: RangeParams & {
      classification?: string;
      exclude_team?: boolean;
    },
  ): Promise<ActiveUserRow[]> => {
    const response = await apiClient.get(`${BASE}/active-users`, { params });
    return response.data;
  },

  getFunnel: async (range: RangeParams): Promise<GrowthFunnel> => {
    const response = await apiClient.get(`${BASE}/funnel`, { params: range });
    return response.data;
  },

  getRetention: async (): Promise<RetentionResponse> => {
    const response = await apiClient.get(`${BASE}/retention`);
    return response.data;
  },
};
